import { getEmployeesURL } from '../../../API/Employee/Employee'
import celebrations from '../../../API/Celebrations/celebrations'
import React, { useEffect, useState, useMemo } from 'react'
import Loading from '../../Loading/Loading'

const Switch = ({ checked, onChange, name, viewOnly = false }) => (
	<label className={`tw-relative tw-inline-flex tw-items-center ${viewOnly ? '' : 'tw-cursor-pointer'}`}>
		<input type='checkbox' name={name} className='tw-sr-only' checked={checked} onChange={onChange} />
		<span className='tw-w-11 tw-h-6 tw-flex tw-items-center tw-bg-gray-200 tw-rounded-full tw-transition-colors'>
			<span
				className={`tw-block tw-w-5 tw-h-5 tw-rounded-full tw-shadow tw-transform tw-transition-transform ${
					checked ? 'tw-bg-green-600 tw-translate-x-5' : 'tw-bg-white tw-translate-x-0'
				}`}
			></span>
		</span>
	</label>
)

function formatDate(dateStr, lang = 'es') {
	if (!dateStr) return '-'
	const date = new Date(dateStr)
	if (isNaN(date)) return '-'
	const options = { day: '2-digit', month: 'short', year: 'numeric' }
	return date.toLocaleDateString(lang, options)
}

const CelebrationSettings = () => {
	const token = localStorage.getItem('token')
	const companyId = JSON.parse(localStorage.getItem('user')).id
	const [settings, setSettings] = useState({
		birthday: false,
		workAnniversary: false,
	})
	const [initialSettings, setInitialSettings] = useState(null)
	const [collaborators, setCollaborators] = useState([])
	const [loading, setLoading] = useState(true)
	const [modalImage, setModalImage] = useState(null)
	const [search, setSearch] = useState('')
	const [sending, setSending] = useState(false)

	const isChanged =
		initialSettings && (settings.birthday !== initialSettings.birthday || settings.workAnniversary !== initialSettings.workAnniversary)

	useEffect(() => {
		const fetchCelebrationData = async () => {
			setLoading(true)
			const response = await celebrations.getData(companyId)
			const employeeImgs = await getEmployeesURL(companyId, token)

			const data = {
				birthday: response.dataValues?.birthday ?? false,
				workAnniversary: response.dataValues?.companyAnniversary ?? false,
				collaborators: Object.values(response)
					.filter(item => typeof item === 'object' && item.id && item.name)
					.map(item => {
						const hasBirth = !!item.birth
						const hasStart = !!item.companyAnniversary
						const isActive = hasBirth && hasStart
						const profileImg =
							employeeImgs.find(img => img.mydnaUserEmail === item.email)?.url || 'https://assets.mydna.company/profile-placeholder.png'
						return {
							id: item.id,
							name: item.name,
							email: item.email,
							photoUrl: profileImg,
							birthDate: item.birth || '',
							startDate: item.companyAnniversary || '',
							active: isActive,
						}
					}),
			}

			setSettings({
				birthday: data.birthday,
				workAnniversary: data.workAnniversary,
			})
			setInitialSettings({
				birthday: data.birthday,
				workAnniversary: data.workAnniversary,
			})
			setCollaborators(data.collaborators || [])
			setLoading(false)
		}

		fetchCelebrationData()
	}, [companyId, token])

	const handleSwitch = e => {
		setSettings({ ...settings, [e.target.name]: e.target.checked })
	}

	const handleColabSwitch = (id, checked) => {
		//desativado por enquanto
		setCollaborators(prev => prev.map(c => (c.id === id ? { ...c, active: checked } : c)))
	}

	const handleConfirm = async () => {
		try {
			setSending(true)
			await celebrations.sendData({
				birthday: settings.birthday,
				companyAnniversary: settings.workAnniversary,
				companyId,
			})
			setInitialSettings(settings)
		} catch (error) {
			console.error('Error', error)
		} finally {
			setSending(false)
		}
	}

	const filteredCollaborators = collaborators.filter(
		c => c.name.toLowerCase().includes(search.toLowerCase()) || c.email.toLowerCase().includes(search.toLowerCase())
	)

	// Detecta idioma do navegador, default espanhol
	const lang = useMemo(() => {
		const navLang = navigator.language || navigator.userLanguage
		if (navLang && navLang.startsWith('pt')) return 'pt-BR'
		return 'es-ES'
	}, [])

	if (loading) return <Loading />

	return (
		<div className='tw-relative tw-flex tw-flex-col  tw-bg-white tw-rounded-lg tw-p-10 tw-mx-auto tw-max-w-6xl border tw-shadow-md'>
			<div className='tw-mb-8 tw-grid tw-grid-cols-2 tw-gap-8'>
				<div>
					<h3 className='tw-mb-2 tw-font-bold tw-text-xl'>Notificaciones de Celebración</h3>
					<div className='tw-text-gray-500 tw-text-sm tw-w-5/6'>
						Al activar las notificaciones, se enviarán automaticamente diariamente emails de feliz cumpleaños y aniversario laboral a todos los
						colaboradores que hayan proporcionados sua fecha de nacimiento y sua fecha de ingreso a la empresa, en sus respectivas fechas.
					</div>
				</div>
				<div className='tw-flex tw-flex-col tw-gap-4 tw-justify-center'>
					<div className='tw-flex tw-items-center tw-justify-between'>
						<span className='tw-text-base'>Enviar correos automáticos de cumpleaños</span>
						<Switch name='birthday' checked={settings.birthday} onChange={handleSwitch} />
					</div>
					<div className='tw-flex tw-items-center tw-justify-between'>
						<span className='tw-text-base'>Enviar correos automáticos de aniversario laboral</span>
						<Switch name='workAnniversary' checked={settings.workAnniversary} onChange={handleSwitch} />
					</div>
					<div className='tw-flex tw-justify-end tw-mt-8'>
						<div
							className={`tw-px-8 tw-py-3 tw-rounded tw-bg-purple-500 tw-text-white tw-font-bold tw-transition tw-duration-200 tw-ease-in-out ${
								!isChanged || sending ? 'tw-opacity-50' : 'tw-cursor-pointer hover:tw-bg-purple-800'
							}`}
							onClick={!isChanged || sending ? undefined : handleConfirm}
						>
							{sending ? 'Cargando...' : 'Confirmar'}
						</div>
					</div>
				</div>
			</div>

			<hr className='tw-my-6' />

			<div className='tw-grid tw-grid-cols-2 tw-gap-8 tw-items-center tw-mb-8'>
				<div>
					<h4 className='tw-font-bold tw-mb-4 tw-text-lg'>Previsualización del Template</h4>
					<p className='tw-text-gray-500 tw-text-sm'>
						Vea ejemplos de plantillas de correo electrónico que se pueden enviar automáticamente a los empleados en fechas especiales.
					</p>
				</div>
				<div>
					<div className='tw-flex tw-gap-6'>
						<img
							src='https://assets.mydna.company/email-birthday.png'
							alt='birthday template'
							className='tw-w-[140px] tw-h-[100px] tw-rounded tw-cursor-pointer tw-object-cover tw-border tw-border-gray-300 hover:tw-shadow'
							onClick={() => setModalImage('https://assets.mydna.company/email-birthday.png')}
						/>
						<img
							src='https://assets.mydna.company/email-birthday-company.png'
							alt='birthday company template'
							className='tw-w-[140px] tw-h-[100px] tw-rounded tw-cursor-pointer tw-object-cover tw-border tw-border-gray-300 hover:tw-shadow'
							onClick={() => setModalImage('https://assets.mydna.company/email-birthday-company.png')}
						/>
					</div>
					{modalImage && (
						<div className='tw-fixed tw-inset-0 tw-bg-black/60 tw-flex tw-items-center tw-justify-center tw-z-50' onClick={() => setModalImage(null)}>
							<img
								src={modalImage}
								alt='Imagem grande'
								className='tw-max-w-[90vw] tw-max-h-[90vh] tw-rounded tw-shadow-lg tw-bg-white'
								onClick={e => e.stopPropagation()}
							/>
						</div>
					)}
				</div>
			</div>

			<hr className='tw-my-6' />

			<div className='tw-flex tw-flex-col tw-gap-8 tw-flex-1'>
				<div>
					<h4 className='tw-font-bold tw-mb-2 tw-text-lg'>¿Quién recibe los correos?</h4>
					<div className='tw-text-gray-500 tw-mb-2 tw-text-sm'>
						A continuación, se muestra la lista de colaboradores que completaron sus respectivas fechas y que recibirán los correos de celebración.
					</div>
				</div>
				<div>
					<div className='border border-grey tw-rounded-lg py-1 tw-flex tw-items-center tw-justify-center tw-mt-1'>
						<input
							type='text'
							placeholder='Buscar por nombre'
							className='tw-mb-4 tw-p-2 tw-rounded border border-0 p-0 m-0 ms-4 tw-w-full'
							value={search}
							onChange={e => setSearch(e.target.value)}
						/>
					</div>
					<div className='tw-rounded tw-p-2 tw-overflow-y-auto tw-max-h-[400px]'>
						{filteredCollaborators.length === 0 && <div className='tw-text-center tw-text-gray-400 tw-py-8'>Nenhum colaborador encontrado.</div>}
						<table className='tw-w-full tw-text-left tw-text-sm tw-bg-white'>
							<thead>
								<tr className='tw-border-b tw-text-gray-500'>
									<th className='tw-px-2 tw-py-2'></th>
									<th className='tw-px-2 tw-py-2'>Nome</th>
									<th className='tw-px-2 tw-py-2'>Email</th>
									<th className='tw-px-2 tw-py-2'>Nascimento</th>
									<th className='tw-px-2 tw-py-2'>Entrada</th>
								</tr>
							</thead>
							<tbody>
								{filteredCollaborators.map(colab => (
									<tr key={colab.id} className=' hover:tw-bg-gray-50'>
										<td className='tw-px-2 tw-py-2'>
											<img src={colab.photoUrl} alt={colab.name} className='tw-w-12 tw-h-12 tw-rounded-full tw-object-cover tw-bg-gray-200' />
										</td>
										<td className='tw-px-2 tw-py-2'>{colab.name}</td>
										<td className='tw-px-2 tw-py-2'>{colab.email}</td>
										<td className='tw-px-2 tw-py-2'>{colab.birthDate ? formatDate(colab.birthDate, lang) : '-'}</td>
										<td className='tw-px-2 tw-py-2'>{colab.startDate ? formatDate(colab.startDate, lang) : '-'}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	)
}

export default CelebrationSettings
